export const CHECKOUT_URL = 'https://moduroadmap-en.impultienda.ar/checkout/';

export const images = {
  mockup: 'https://cdn.magicpatterns.com/uploads/qbpQWscZPVPTAd4knoXngs/MOCKUP_MODUROADMAP-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/cbfUa5EmmKVrV3dywCx3v1/DR._ELOWEN_VARELLI_-_SENSORY.png',
};

export const roadmapSteps = [
  { title: 'Identify Functional Priorities', text: 'Clarify which daily activities, routines, or participation challenges should guide intervention planning.' },
  { title: 'Understand Sensory-Related Needs', text: 'Organize relevant observations, assessment findings, environmental factors, and individual responses.' },
  { title: 'Define Intervention Goals', text: 'Translate functional concerns into specific, observable, individualized goals.' },
  { title: 'Explore Intervention Options', text: 'Review potentially appropriate sensory regulation supports, environmental modifications, and occupation-focused strategies.' },
  { title: 'Structure the Treatment Plan', text: 'Organize selected strategies into a practical framework for clinical sessions, home routines, or school participation.' },
  { title: 'Monitor and Adjust', text: 'Document responses, review functional outcomes, and identify when a strategy or treatment plan requires modification.' },
];

export const planningQuestions = [
  'Which functional concern should you prioritize?',
  'Which environmental factors should you address first?',
  "How do you choose strategies that match the child's actual needs?",
  'How do you organize your sessions?',
  'How do you determine whether your intervention is supporting meaningful participation?',
];

export const beforeItems = [
  "You finish an assessment but aren't sure how to organize your next steps.",
  'You have several possible intervention strategies but struggle to decide which ones deserve consideration.',
  'You spend valuable time searching through books, notes, and previous treatment plans.',
  'You sometimes choose activities before clearly defining the functional goal.',
  'You find it challenging to explain why a particular strategy may be appropriate.',
  'Your treatment planning process changes from case to case.',
  'You struggle to connect session activities with meaningful changes in everyday participation.',
  'You want more structure without relying on rigid, one-size-fits-all protocols.',
];

export const afterItems = [
  'You begin with clearly defined functional priorities.',
  'You organize relevant sensory observations and contextual information.',
  'You identify measurable goals before selecting intervention activities.',
  'You use decision-support worksheets to explore potential intervention options.',
  "You consider the child's individual needs, preferences, environment, and responses.",
  'You organize session plans using repeatable templates.',
  'You document observable responses and functional outcomes.',
  'You recognize when additional assessment, collaboration, or adjustment may be necessary.',
  'You follow a structured planning framework while maintaining individualized clinical judgment.',
];

export const painPoints = [
  {
    title: "You Know Sensory Strategies, but You're Unsure Which Ones to Prioritize",
    body: "You've learned about sensory regulation approaches, environmental adaptations, and activity-based interventions. But knowing several options doesn't automatically tell you which approach deserves consideration for a particular child.",
    need: 'You need a clearer way to connect intervention options with individualized functional needs.',
  },
  {
    title: "Your Treatment Planning Takes Longer Than You'd Like",
    body: "You review assessment notes, search through reference materials, and organize possible activities. Before you know it, you've spent considerable time preparing a plan that still feels incomplete.",
    need: 'You want a practical structure that helps you organize your thinking more efficiently.',
  },
  {
    title: "You Have Activities, but Your Functional Goals Aren't Always Clear",
    body: 'You know what activities you could use. But explaining exactly what each activity is intended to support can be challenging.',
    need: 'You want to connect intervention planning with meaningful participation outcomes.',
  },
  {
    title: 'Every Child Responds Differently',
    body: 'An approach that appears useful for one child may be unsuitable or ineffective for another.',
    need: 'You need a framework that encourages individualization, careful observation, and ongoing adjustment.',
  },
  {
    title: 'You Want to Explain Your Intervention Decisions More Clearly',
    body: 'Families and educators may ask why a particular strategy is being considered.',
    need: 'You want to communicate the relationship between functional concerns, intervention goals, and selected supports.',
  },
  {
    title: 'You Want a More Repeatable Clinical Planning Process',
    body: "You don't want to rebuild your planning structure every time you encounter a new case.",
    need: 'You want a consistent organizational framework that remains flexible enough for individualized care.',
  },
];

export type Volume = {
  numeral: string;
  title: string;
  hook: string;
  intro: string;
  itemsLabel: string;
  items: string[];
  extra?: string;
  framework?: string[];
  goal: string;
};

export const volumes: Volume[] = [
  {
    numeral: 'I',
    title: 'From Assessment Findings to Intervention Priorities',
    hook: "Start With What Matters Most in the Child's Everyday Life.",
    intro: "Before choosing intervention activities, you need to understand what you're trying to support. This volume helps you organize assessment findings and identify functional priorities that may guide intervention planning.",
    itemsLabel: "Inside, you'll explore",
    items: [
      'Organizing relevant assessment information',
      'Identifying participation challenges',
      'Reviewing sensory-related observations',
      'Understanding environmental influences',
      'Considering child and family priorities',
      'Distinguishing observations from interpretations',
      'Identifying missing clinical information',
      'Establishing initial intervention priorities',
    ],
    extra: "You'll also receive practical worksheets to organize functional concerns and document the information that may influence planning decisions.",
    goal: 'Move from scattered assessment information to clearly organized intervention priorities.',
  },
  {
    numeral: 'II',
    title: 'The Sensory Modulation Intervention Decision Map™',
    hook: 'Explore Intervention Options Without Relying on Guesswork.',
    intro: "Once you've identified functional priorities, the next challenge is deciding which intervention approaches deserve consideration. This volume introduces a visual decision-support framework for exploring sensory-related intervention options.",
    itemsLabel: "You'll learn to organize",
    items: [
      'Individual sensory response patterns',
      'Relevant environmental demands',
      'Functional participation barriers',
      'Child preferences and tolerances',
      'Possible sensory regulation supports',
      'Environmental modification options',
      'Occupation-focused strategies',
      'Safety considerations and contraindications',
      'Alternative approaches when initial strategies are unsuitable',
    ],
    extra: "You'll receive visual decision maps and comparison worksheets to help structure your reasoning. These tools are not automatic treatment selectors — they support educational planning alongside appropriate assessment, professional judgment, and current evidence.",
    goal: 'Understand how to explore intervention options systematically instead of selecting activities based on familiarity alone.',
  },
  {
    numeral: 'III',
    title: 'The Pediatric Sensory Regulation Strategy Library',
    hook: 'Organize Practical Strategies Around Functional Needs.',
    intro: 'Sensory regulation supports can take different forms depending on the child, activity, environment, and intended outcome. This volume presents an organized educational library of intervention options and contextual adaptations.',
    itemsLabel: 'Explore strategies related to',
    items: [
      'Environmental adjustments',
      'Predictable routines and transitions',
      'Movement and activity participation',
      'Tactile experiences and accommodations',
      'Auditory environmental supports',
      'Visual environmental modifications',
      'Co-regulation and caregiver support',
      'Participation during daily routines',
      'Individualized activity adaptation',
      'Monitoring comfort, tolerance, and response',
    ],
    extra: 'Each strategy category includes educational guidance on its possible purpose, relevant considerations, and questions to explore before implementation.',
    goal: 'Build a more organized reference of potential intervention options and understand the considerations that influence their use.',
  },
  {
    numeral: 'IV',
    title: 'The Sensory Modulation Treatment Planning Matrix™',
    hook: 'Connect Functional Goals, Intervention Strategies, and Clinical Reasoning.',
    intro: 'This volume introduces the central planning tool of the system. The Matrix helps you organize the relationship between what you want to support and how you intend to approach it.',
    itemsLabel: "You'll learn to document",
    items: [
      'The primary functional concern',
      'Relevant assessment findings',
      'Individualized intervention goals',
      'Selected intervention options',
      'The clinical rationale for each option',
      'Environmental adaptations',
      'Required resources and supports',
      'Potential safety considerations',
      'Observable response indicators',
      'Criteria for reviewing or modifying the plan',
    ],
    extra: "You'll also receive printable planning matrices designed to make your reasoning easier to review and communicate.",
    goal: 'Create an organized intervention plan that connects clinical decisions with functional participation.',
  },
  {
    numeral: 'V',
    title: 'From Treatment Planning to Functional Participation',
    hook: 'Organize Interventions Around Meaningful Everyday Activities.',
    intro: "A structured intervention plan should remain connected to the child's real-life participation. This volume focuses on translating intervention priorities into practical, context-specific planning.",
    itemsLabel: "You'll explore",
    items: [
      'Structuring occupation-focused intervention goals',
      'Organizing session activities',
      'Planning environmental adaptations',
      'Supporting transitions and daily routines',
      'Collaborating with families and educators',
      'Adapting strategies across contexts',
      'Documenting child preferences and responses',
      'Identifying observable participation changes',
      'Reviewing whether intervention activities remain relevant',
    ],
    framework: ['Functional Goal', 'Context', 'Intervention Option', 'Observable Response'],
    goal: "Keep the child's participation at the center of the treatment planning process.",
  },
  {
    numeral: 'VI',
    title: 'The Complete Sensory Modulation Treatment Roadmap™',
    hook: 'Bring Everything Together Into One Repeatable Clinical Planning Workflow.',
    intro: "The final volume integrates the previous five volumes into a complete educational planning process. You'll learn how to work through eight stages:",
    itemsLabel: 'The eight stages',
    items: [
      'Review the functional concern',
      'Organize relevant assessment findings',
      'Establish functional priorities',
      'Explore appropriate intervention options',
      'Select and document planning decisions',
      'Structure the intervention plan',
      'Monitor functional responses',
      'Review and adjust the plan',
    ],
    extra: "You'll also learn to use the complete planning worksheets to organize an initial intervention plan in a focused 20-minute exercise when sufficient assessment information is already available.",
    goal: 'Have a practical reference you can return to whenever you need to organize an individualized sensory modulation intervention plan.',
  },
];

export const toolkit = [
  { title: 'Sensory Intervention Decision Trees', text: 'Visual frameworks for organizing functional concerns, relevant information, and possible intervention considerations.' },
  { title: 'Individualized Treatment Planning Matrices', text: 'Printable templates for connecting functional goals with intervention options, clinical rationale, and review criteria.' },
  { title: 'Sensory Regulation Strategy Selection Checklists', text: 'Educational prompts to help you consider individual needs, environmental factors, preferences, and safety before choosing strategies.' },
  { title: 'Functional Goal Development Worksheets', text: 'Structured templates for translating participation concerns into observable, context-specific intervention goals.' },
  { title: 'Intervention Progress Monitoring Sheets', text: 'Practical forms for documenting responses, functional observations, and decisions about reviewing or adjusting a plan.' },
  { title: 'Session Planning Templates', text: 'Organizational tools for structuring session objectives, selected activities, adaptations, and follow-up observations.' },
  { title: 'Home and School Adaptation Worksheets', text: 'Templates for organizing environmental supports and communicating proposed adaptations with caregivers and educators.' },
  { title: 'Clinical Intervention Review Checklists', text: 'Prompts for evaluating whether selected strategies remain appropriate, relevant, and aligned with functional goals.' },
];

export const bonuses = [
  {
    number: 1,
    title: 'The 20-Minute Sensory Intervention Planner™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/1rDFyyrCaMUZSm5HTTY5oF/BONUS_1_MODUROADMAP-EN.png',
    intro: "When you've already gathered assessment information, you may still need a practical way to organize your initial intervention plan. This printable planning workbook provides a focused structure for bringing the essential elements together.",
    items: [
      'Initial intervention planning worksheet',
      'Functional priority organizer',
      'Sensory-related findings summary',
      'Intervention option comparison sheet',
      'Individualized goal planner',
      'Clinical rationale documentation template',
      'Progress review planning sheet',
    ],
    note: 'The timeframe applies to drafting and organizing a plan, not completing the individualized clinical decision-making process.',
  },
  {
    number: 2,
    title: 'The Pediatric Sensory Strategy Selection Guide™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/hFLoyfQzaC4QDzejfi39aj/BONUS_2_MODUROADMAP-EN.png',
    intro: 'Not every sensory-related strategy is appropriate for every child. This educational reference helps you explore the factors that may influence intervention selection.',
    items: [
      'Strategy comparison tables',
      'Environmental consideration checklists',
      'Child preference and tolerance worksheets',
      'Activity adaptation prompts',
      'Safety consideration guides',
      'Functional relevance questions',
      'Intervention review templates',
    ],
    note: 'Encourages thoughtful, individualized planning rather than automatic selection of sensory activities.',
  },
  {
    number: 3,
    title: 'The Sensory Intervention Session Planner™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/dT8eKaimhiD8h4rbYNqUvC/BONUS_3_MODUROADMAP-EN.png',
    intro: 'A collection of practical templates designed to help you organize pediatric occupational therapy sessions.',
    items: [
      'Session objective worksheets',
      'Activity planning templates',
      'Environmental preparation checklists',
      'Adaptation planning forms',
      'Child response documentation sheets',
      'Session reflection prompts',
      'Follow-up planning templates',
    ],
    note: "Keep your session planning connected to the child's individualized functional goals.",
  },
  {
    number: 4,
    title: '15 Pediatric Sensory Intervention Planning Challenges™',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/sYfKb6izRTheoRm8P27LeH/BONUS_4_MODUROADMAP-EN.png',
    intro: "Practice applying the Roadmap through 15 fictional educational scenarios, each presenting a pediatric participation concern involving possible sensory modulation considerations. For each case, you'll explore:",
    items: [
      'The primary functional concern',
      'Available assessment information',
      'Relevant sensory-related observations',
      'Contextual and environmental factors',
      'Possible intervention priorities',
      'Potential intervention options',
      'Alternative explanations',
      'Safety and individualization considerations',
      'Functional outcome indicators',
    ],
    note: 'Each challenge includes reflection questions and an educational planning review. These are fictional learning exercises, not validated treatment protocols.',
  },
  {
    number: 5,
    title: 'The Functional Outcomes & Treatment Progress Tracker™',
    value: 17,
    image: 'https://cdn.magicpatterns.com/uploads/6irN9YGKHYoNtos7ajVnfX/BONUS_5_MODUROADMAP-EN.png',
    intro: "Organizing a treatment plan is only the beginning. You also need a practical way to document how the child's participation changes over time.",
    items: [
      'Functional baseline templates',
      'Participation observation sheets',
      'Intervention response trackers',
      'Goal review worksheets',
      'Session progress documentation forms',
      'Family communication templates',
      'Treatment adjustment records',
      'Functional outcome review checklists',
    ],
    note: 'Supports organized documentation without assuming that every observed change was caused by a sensory intervention.',
  },
];

export const testimonials = [
  {
    name: 'Sofía Ramírez',
    role: 'Pediatric Occupational Therapist',
    topic: 'Intervention Planning',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/334deb13-7c14-4d8c-8083-7b60156693ee.jpg',
    quote: 'Having a clear framework for organizing intervention priorities makes it so much easier to connect assessment findings with functional goals instead of jumping straight into activity selection.',
  },
  {
    name: 'Laura Bennett',
    role: 'Pediatric OT, Private Practice',
    topic: 'Clinical Organization',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/a6391e7e-f5d2-46eb-b630-9190d22780c7.jpg',
    quote: "The planning matrix helps me document why I'm considering a particular strategy and what information I still need before moving forward.",
  },
  {
    name: 'Daniel Okafor',
    role: 'Occupational Therapist',
    topic: 'Pediatric Sessions',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/79516338-1d21-4371-a78a-a634075d04a9.jpg',
    quote: 'The session planning templates that connect activities to participation goals are genuinely useful when preparing individualized occupational therapy sessions.',
  },
  {
    name: 'Mei Tanaka',
    role: 'Occupational Therapy Student',
    topic: 'Professional Learning',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/1eab6b21-78ce-454d-ade6-07ed146e9106.jpg',
    quote: 'Working through the fictional cases helped me practice comparing intervention options and recognizing when additional assessment information was necessary.',
  },
  {
    name: 'Rachel Lindqvist',
    role: 'Pediatric OT, School Setting',
    topic: 'Functional Progress',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/6193de55-8c62-46fa-9efd-78ab4bf2ebb2.jpg',
    quote: 'Having a consistent way to document functional outcomes makes my treatment reviews easier to organize and communicate to families and teachers.',
  },
  {
    name: 'David Morales',
    role: 'Pediatric Occupational Therapist',
    topic: 'Communicating With Families',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/8715e19c-cc98-465f-8dd3-345b83dc16a2.jpg',
    quote: 'When parents ask why I chose a particular strategy, I can now walk them through the functional goal, the context, and what we will observe. The conversation is much clearer.',
  },
];

export const faqs = [
  {
    q: 'How will I receive the materials?',
    a: 'The Sensory Modulation Treatment Roadmap™ is a digital educational product. Right after your purchase is confirmed, you will receive access to the six premium volumes and five professional bonuses through the delivery method provided at checkout (sent to the email you use to purchase).',
  },
  {
    q: 'Is this a course?',
    a: 'No. It is a collection of digital educational guides, visual frameworks, planning worksheets, and professional reference tools, designed for independent reading and practical educational use.',
  },
  {
    q: 'Who is this product for?',
    a: 'It is designed primarily for pediatric occupational therapists and occupational therapy students who want a more organized approach to learning about sensory modulation intervention planning. Students and less-experienced practitioners should use it within the boundaries of their training and appropriate professional supervision.',
  },
  {
    q: 'Do I need previous experience with sensory integration?',
    a: 'Familiarity with pediatric occupational therapy assessment and sensory processing concepts is recommended. The materials explain the planning framework, but they do not replace foundational professional education, supervised clinical experience, or specialist training.',
  },
  {
    q: 'Does this product provide standardized treatment protocols?',
    a: 'No. The Roadmap provides educational planning frameworks and decision-support resources. It does not prescribe universal treatment protocols or automatically determine the appropriate intervention for a child.',
  },
  {
    q: 'Can I use these tools with my pediatric patients?',
    a: 'The materials may be used as educational organizational aids within your professional scope of practice. Clinical decisions must remain individualized and should consider appropriate assessment, current evidence, safety, professional standards, and relevant local regulations.',
  },
  {
    q: 'Does the Roadmap guarantee better treatment outcomes?',
    a: "No. Clinical outcomes depend on many factors, including the child's individual characteristics, functional needs, intervention appropriateness, environment, and broader care. The product supports organized planning and professional learning, not guaranteed results.",
  },
  {
    q: 'Can I print the materials?',
    a: 'Yes. The printable worksheets and templates are intended for personal professional use. Please follow the applicable product license and avoid entering identifiable patient information into unsecured documents or systems.',
  },
  {
    q: 'Can I use this product outside the United States?',
    a: 'Yes. The educational planning concepts may be relevant to professionals in different countries. However, clinical standards, scopes of practice, documentation requirements, and regulations vary by location — always follow the requirements of your jurisdiction. Your price is automatically converted to your local currency at checkout.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. The listed purchase price is USD $27, with no monthly subscription.',
  },
  {
    q: 'How long will I have access?',
    a: 'The offer includes lifetime access to the purchased digital materials, subject to the applicable purchase and delivery terms.',
  },
  {
    q: 'What if I change my mind?',
    a: 'Your purchase is covered by a 7-day refund policy. Please review the refund terms displayed at checkout for eligibility, deadlines, and instructions.',
  },
];

export const finalChecklist = [
  'What the child needs',
  'Which functional goals matter',
  'Which intervention options deserve consideration',
  'How to structure your plan',
  'What to observe during implementation',
  'When to review and adjust',
];

export const recentBuyers = [
  { name: 'María González', country: 'Mexico' },
  { name: 'Lucía Fernández', country: 'Spain' },
  { name: 'Valentina Rojas', country: 'Chile' },
  { name: 'Camila Herrera', country: 'Colombia' },
  { name: 'Sofía Martínez', country: 'Argentina' },
  { name: 'Andrea Castillo', country: 'Peru' },
  { name: 'Daniela Vargas', country: 'Costa Rica' },
  { name: 'Gabriela Torres', country: 'Ecuador' },
  { name: 'Carolina Méndez', country: 'Uruguay' },
  { name: 'Isabel Romero', country: 'Puerto Rico' },
  { name: 'Natalia Pérez', country: 'Dominican Republic' },
  { name: 'Paula Jiménez', country: 'Guatemala' },
  { name: 'Mariana Silva', country: 'Paraguay' },
  { name: 'Javier Morales', country: 'Panama' },
  { name: 'Alejandra Cruz', country: 'Bolivia' },
  { name: 'Fernanda López', country: 'Venezuela' },
];
